import React, { useState, useMemo } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  useInput,
  usePathParam,
  useViewport
} from '@chilecompra/react-kit'
import { Button, Dialog, Typography } from '@chilecompra/react-kit/components'

import { PUBLIC_URL } from '../../config/settings/environment'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import TextArea from '../../components/Inputs/TextArea'
import useReCaptcha from '../../modules/hooks/reCaptcha'
import { USER_TYPE } from '../../config/settings/constant'

import {
  onGetAllSanctionsThunk,
  onGetContractualBehaviorSummaryThunk,
  onGetSanctionsByProcessTypeThunk
} from '../ContractualBehavior/ContractualBehavior.actions'
import { onCloseFeedbackDialogThunk, onOpenRevokeDialogSanction, onWithdrawSanctionThunk } from './Sanction.actions'

import { Box, Column, Strong, Wrapper } from './SanctionFooter.styles'
import { DialogContent, DialogIcon, DialogMessage } from './Dialogs/SanctionDialog.styles'

import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

import SanctionDetailsRevokeDialog from './Dialogs/SanctionDetailsRevoke.dialog'

/**
 * The Sanction Footer's container.
 */
const SanctionDetailsFooter = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    size: { isUpToMedium }
  } = useViewport()
  const dni = usePathParam('rut')
  const sanctionID = usePathParam('id')

  const {
    canWithdraw,
    canRevoke,
    retiroVigente,
    loading,
    error,
    success,
    showFeedbackDialog,
    poBiddingInfo: { error: purchaseOrderError, loading: purchaseOrderLoading, origin }
  } = useSelector(state => state.sanction)
  const { loadingAllSanctions } = useSelector(state => state.contractualBehavior)
  const { error: sellerError, orgLegalName } = useSelector(state => state.basicSellerInformation)

  const [showDialog, setShowDialog] = useState(false)

  const {
    error: reasonError,
    onChange: handleReasonChange,
    onReset: handleReasonReset,
    value: reasonValue
  } = useInput({
    initialValue: '',
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(500)]
  })

  const handleWithdraw = () => {
    dispatch(
      onWithdrawSanctionThunk({
        id: sanctionID,
        justification: reasonValue,
        callback: () => setShowDialog(false)
      })
    )
  }

  const handleDialogClose = () => {
    if (!loading) {
      setShowDialog(false)
    }
  }

  // Used for success and error modal
  const handleFeedbackDialogClose = async () => {
    if (!success) {
      return dispatch(onCloseFeedbackDialogThunk())
    }
    batch(() => {
      dispatch(onCloseFeedbackDialogThunk())
      if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
        const executeAll = async () => {
          await executeReCaptcha({ action: 'publicInfo' })
          dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
        }

        executeAll()
      } else {
        dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
      }

      if (origin === undefined || origin === '' || loadingAllSanctions) {
        if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
          const executeAll = async () => {
            await executeReCaptcha({ action: 'publicInfo' })
            dispatch(onGetAllSanctionsThunk({ dni }))
          }

          executeAll()
        } else {
          dispatch(onGetAllSanctionsThunk({ dni }))
        }
      } else {
        if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
          const executeAll = async () => {
            await executeReCaptcha({ action: 'publicInfo' })
            dispatch(onGetSanctionsByProcessTypeThunk({ dni, processType: origin }))
          }

          executeAll()
        } else {
          dispatch(onGetSanctionsByProcessTypeThunk({ dni, processType: origin }))
        }
      }
    })
    navigate(routes.contractualBehavior.replace(PATH_PARAM, dni))
  }

  const handleClickBack = () => {
    navigate(-1)
  }

  const handleShowDialog = () => {
    handleReasonReset()
    setShowDialog(true)
    window.scrollTo(0, 0)
  }

  const handleOpenRevokeDialog = () => {
    dispatch(onOpenRevokeDialogSanction())
  }

  const handleRevokeSuccess = () => {
    const refetchSummary = () => dispatch(onGetContractualBehaviorSummaryThunk({ dni }))
    const refetchSanctions = () =>
      origin === undefined || origin === '' || loadingAllSanctions
        ? dispatch(onGetAllSanctionsThunk({ dni }))
        : dispatch(onGetSanctionsByProcessTypeThunk({ dni, processType: origin }))

    if (document.getElementById('recaptcha') && userType === USER_TYPE.PUBLIC) {
      const executeAll = async () => {
        await executeReCaptcha({ action: 'publicInfo' })
        batch(() => {
          refetchSummary()
          refetchSanctions()
        })
      }
      executeAll()
    } else {
      batch(() => {
        refetchSummary()
        refetchSanctions()
      })
    }
  }

  const canCancelSanction = useMemo(() => {
    return !purchaseOrderError && !purchaseOrderLoading && !sellerError && canWithdraw
  }, [purchaseOrderError, purchaseOrderLoading, sellerError, canWithdraw])

  const canRevokeSanction = useMemo(() => {
    return !purchaseOrderError && !purchaseOrderLoading && !canWithdraw && canRevoke && !retiroVigente
  }, [purchaseOrderError, purchaseOrderLoading, canWithdraw, canRevoke, retiroVigente])

  return (
    <>
      <Column item xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
        <Wrapper>
          <Button color="default" variant="outlined" onClick={handleClickBack}>
            Volver
          </Button>
          {canCancelSanction && (
            <Button color="error" variant="contained" onClick={handleShowDialog}>
              Anular sanción
            </Button>
          )}
          {canRevokeSanction && (
            <Button color="error" variant="contained" onClick={handleOpenRevokeDialog}>
              Dejar sin efecto
            </Button>
          )}
        </Wrapper>
      </Column>

      {/* Confirmation Modal */}
      <Dialog disableBackdropClick maxWidth="530px" open={showDialog} onClose={handleDialogClose}>
        <DialogContent>
          <DialogIcon alt="Cambios guardados" src={`${PUBLIC_URL}/assets/images/circle-warning-icon.svg`} />
          <DialogMessage>
            <Typography fontWeight="bold" variant="h4">
              {`Estás a punto de eliminar la sanción para el proveedor ${orgLegalName}`}
            </Typography>
            <Typography margin="16px 0" variant="body2">
              Una vez anulada, la sanción dejará de estar visible en la ficha del proveedor. La nota del comportamiento
              del proveedor será modificada en un <Strong>plazo máximo de 5 días hábiles.</Strong>
            </Typography>
            <Box>
              <TextArea
                error={reasonError}
                label="Justificación"
                maxCharactersForCounter={500}
                maxLength={500}
                rows={5}
                showCounter
                value={reasonValue}
                width="100%"
                onChange={handleReasonChange}
              />
            </Box>
            <Button
              color="error"
              disabled={loading || reasonValue === '' || reasonValue.trim() === ''}
              loading={loading}
              margin="28px 0 0"
              variant="contained"
              onClick={handleWithdraw}
            >
              Confirmar y anular sanción
            </Button>
          </DialogMessage>
        </DialogContent>
      </Dialog>
      {/* Feedback Modal */}
      {showFeedbackDialog && (
        <Dialog maxWidth="430px" open={showFeedbackDialog} onClose={handleFeedbackDialogClose}>
          <DialogContent>
            {success && (
              <DialogIcon alt="Sanción eliminada" src={`${PUBLIC_URL}/assets/images/circle-success-icon.svg`} />
            )}
            <DialogMessage>
              <Typography fontWeight="bold" variant="h4">
                {error && 'Lo sentimos, la sanción no pudo ser eliminada'}
                {success && 'Sanción anulada correctamente'}
              </Typography>
              <Typography margin="16px 0 0" variant="body2">
                {error && 'Por favor, inténtalo en unos minutos más.'}
                {success &&
                  'La sanción ha dejado de ser visible en la ficha del proveedor. La nota del comportamiento del proveedor será modificada en un plazo máximo de '}
                {success && <Strong>5 días hábiles.</Strong>}
              </Typography>
              <Button color="default" margin="28px 0 0" variant="contained" onClick={handleFeedbackDialogClose}>
                {success && 'Aceptar'}
                {error && 'Volver'}
              </Button>
            </DialogMessage>
          </DialogContent>
        </Dialog>
      )}
      {/* Sanction revoke Modal */}
      <SanctionDetailsRevokeDialog onRevokeSuccess={handleRevokeSuccess} />
    </>
  )
}

export default SanctionDetailsFooter
